import { Link } from 'react-router-dom';
import { Dumbbell, MessageCircle } from 'lucide-react';

import { En } from '../i18n/En';
import { formatDate, formatMonthShort } from '../i18n/format';
import { t } from '../i18n/t';
import { WEEKS } from './charts.ts';
import { patternLabel } from './practice.ts';
import { statusLabel } from './progress.ts';
import type { GridCell, NextUp, PatternRow, Totals } from './progressOverview.ts';

/** One action, picked by `nextUp`: the busiest pattern not yet got right, else a conversation. */
export function NextUpCard({
  next,
  rows,
  lastTalk,
  onPractise,
  busy = false,
}: {
  next: NextUp;
  rows: readonly PatternRow[];
  lastTalk: string | null;
  /** On the Practice screen, start the conversation here rather than open the pattern. */
  onPractise?: (itemId: string) => void;
  busy?: boolean;
}) {
  const row = next.kind === 'practise' ? rows.find((r) => r.id === next.itemId) : undefined;
  return (
    <section className="bg-primary/5 border-primary/30 flex flex-col gap-3 rounded-box border-2 p-4">
      <span className="text-primary text-xs font-semibold tracking-wide uppercase">{t('progress-next-up')}</span>
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className="bg-primary text-primary-content grid size-10 shrink-0 place-items-center rounded-xl"
        >
          {row ? <Dumbbell className="size-5" /> : <MessageCircle className="size-5" />}
        </span>
        <div className="flex flex-col">
          <span className="text-lg font-bold">
            {row ? t('progress-next-practise', { pattern: patternLabel(row.pattern) }) : t('progress-next-talk')}
          </span>
          <span className="text-base-content/70 text-sm">
            {row ? t('progress-next-practise-why', { count: row.occurrences }) : t('progress-next-talk-why')}
          </span>
        </div>
      </div>
      {row && onPractise ? (
        <button
          type="button"
          className="btn btn-primary min-h-(--hit-min) w-full"
          disabled={busy}
          onClick={() => onPractise(row.id)}
        >
          {busy ? t('common-starting') : t('practice-start')}
        </button>
      ) : (
        <Link
          to={row ? `/practice/${encodeURIComponent(row.id)}` : '/session'}
          className="btn btn-primary min-h-(--hit-min) w-full"
        >
          {row ? t('practice-start') : t('progress-next-talk')}
        </Link>
      )}
      {lastTalk && (
        <span className="text-base-content/60 text-center text-xs">{t('progress-last-talk', { when: lastTalk })}</span>
      )}
    </section>
  );
}

function Tile({ value, label, note }: { value: number; label: string; note?: string }) {
  return (
    <div className="bg-base-100 border-base-200 flex flex-col rounded-box border p-3">
      <span className="text-3xl font-bold tabular-nums">{value}</span>
      <span className="text-base-content/70 text-sm">{label}</span>
      {note && <span className="text-base-content/50 text-xs">{note}</span>}
    </div>
  );
}

/** The window's four numbers. Totals, not "recent": the headline used to be a capped list's length. */
export function TotalsTiles({ totals }: { totals: Totals }) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-base-content/60 text-sm font-semibold">{t('progress-window', { weeks: WEEKS })}</h2>
      <div className="grid grid-cols-2 gap-2">
        <Tile value={totals.conversations} label={t('progress-tile-conversations')} />
        <Tile
          value={totals.minutes}
          label={t('progress-tile-minutes')}
          note={totals.wpm !== null ? t('progress-tile-pace', { wpm: totals.wpm }) : undefined}
        />
        <Tile value={totals.lessonDays} label={t('progress-tile-lesson-days')} />
        <Tile value={totals.words} label={t('progress-tile-words')} />
      </div>
    </section>
  );
}

const SHADE = ['bg-base-200', 'bg-primary/25', 'bg-primary/50', 'bg-primary/75', 'bg-primary'];

function asDate(localDay: string): Date {
  const [y, m, d] = localDay.split('-').map(Number);
  return new Date(y as number, (m as number) - 1, d);
}

/**
 * Twelve weeks of days, shaded by conversations and lesson plays together.
 * No run count beside it: a streak is a guilt mechanic (roadmap).
 */
export function ActivityCalendar({ grid }: { grid: GridCell[][] }) {
  return (
    <section className="bg-base-100 border-base-200 flex flex-col gap-3 rounded-box border p-4">
      <h2 className="font-semibold">{t('progress-activity-title')}</h2>
      <div className="flex gap-1">
        <div aria-hidden className="text-base-content/50 grid grid-rows-[auto_repeat(7,1fr)] gap-1 text-[0.625rem]">
          <span className="h-3" />
          {[0, 1, 2, 3, 4, 5, 6].map((d) => (
            <span key={d} className="flex items-center">
              {d % 2 === 0 ? formatDate(asDate(grid[0]?.[d]?.localDay ?? '2026-01-05'), { weekday: 'narrow' }) : ''}
            </span>
          ))}
        </div>
        <ol className="grid flex-1 grid-cols-12 gap-1">
          {grid.map((week, w) => {
            const first = week[0];
            const prev = grid[w - 1]?.[0];
            const month = first && (!prev || asDate(first.localDay).getMonth() !== asDate(prev.localDay).getMonth());
            return (
              <li key={first?.localDay ?? w} className="grid grid-rows-[auto_repeat(7,1fr)] gap-1">
                <span aria-hidden className="text-base-content/50 h-3 text-[0.625rem] leading-3 whitespace-nowrap">
                  {month && first ? formatMonthShort(asDate(first.localDay)) : ''}
                </span>
                {week.map((c) => (
                  <span
                    key={c.localDay}
                    role={c.future ? undefined : 'img'}
                    aria-label={
                      c.future
                        ? undefined
                        : t('progress-activity-day', {
                            date: formatDate(asDate(c.localDay), { weekday: 'short', day: 'numeric', month: 'short' }),
                            talks: c.conversations,
                            lessons: c.lessonPlays,
                          })
                    }
                    className={`aspect-square rounded-[0.25rem] ${c.future ? '' : SHADE[c.level]} ${
                      c.isToday ? 'ring-primary ring-1 ring-offset-1' : ''
                    }`}
                  />
                ))}
              </li>
            );
          })}
        </ol>
      </div>
      <div aria-hidden className="text-base-content/50 flex items-center justify-end gap-1 text-xs">
        {t('progress-activity-less')}
        {SHADE.map((s) => (
          <span key={s} className={`size-3 rounded-[0.2rem] ${s}`} />
        ))}
        {t('progress-activity-more')}
      </div>
    </section>
  );
}

const DOT: Record<string, { cls: string; label: () => string }> = {
  USED_CORRECTLY: { cls: 'bg-primary', label: () => t('mark-got') },
  RECURRED: { cls: 'bg-warning', label: () => t('mark-back') },
  TARGETED: { cls: 'border-base-content/30 border-2', label: () => t('mark-missed') },
};

export function StatusBadge({ latest }: { latest: string | null }) {
  const tone = latest === 'USED_CORRECTLY' ? 'badge-primary' : latest === 'RECURRED' ? 'badge-warning' : 'badge-ghost';
  return (
    <span className={`badge badge-sm shrink-0 ${tone}`}>
      {latest ? statusLabel(latest) : t('progress-pattern-new')}
    </span>
  );
}

export function OutcomeDots({ recent }: { recent: readonly string[] }) {
  return (
    <span className="flex gap-1">
      {recent.map((o, i) => {
        const dot = DOT[o];
        return dot ? (
          <span key={i} role="img" aria-label={dot.label()} className={`size-3 rounded-full ${dot.cls}`} />
        ) : null;
      })}
    </span>
  );
}

export function DotLegend() {
  return (
    <div aria-hidden className="text-base-content/50 flex flex-wrap gap-3 text-xs">
      {Object.values(DOT).map((d) => (
        <span key={d.cls} className="flex items-center gap-1">
          <span className={`size-2.5 rounded-full ${d.cls}`} />
          {d.label()}
        </span>
      ))}
    </div>
  );
}

/**
 * The busiest patterns, each with its correction and its last few outcomes
 * as dots, oldest first: one list where there were two (the strips chart and
 * "Working on") saying the same thing in different shapes.
 */
export function PatternList({ rows, more }: { rows: readonly PatternRow[]; more: number }) {
  if (rows.length === 0) return null;
  return (
    <section className="flex flex-col gap-2">
      <h2 className="font-semibold">{t('progress-patterns-title')}</h2>
      <ul className="flex flex-col gap-2">
        {rows.map((r) => (
          <li key={r.id}>
            <Link
              to={`/practice/${encodeURIComponent(r.id)}`}
              className="bg-base-100 border-base-200 hover:border-primary/40 flex flex-col gap-1 rounded-box border p-3"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">{patternLabel(r.pattern)}</span>
                <StatusBadge latest={r.latest} />
              </div>
              {r.learnerForm && r.targetForm && (
                <p className="text-base-content/70 text-sm">
                  <En>
                    <s>{r.learnerForm}</s> → {r.targetForm}
                  </En>
                </p>
              )}
              <div className="flex items-center justify-between gap-2">
                <span className="text-base-content/50 text-xs">{t('practice-seen', { count: r.occurrences })}</span>
                <OutcomeDots recent={r.recent} />
              </div>
            </Link>
          </li>
        ))}
      </ul>
      <DotLegend />
      {more > 0 && (
        <Link to="/practice" className="link link-primary min-h-(--hit-min) self-start text-sm">
          {t('progress-more', { count: more })}
        </Link>
      )}
    </section>
  );
}
